import { useEffect, useState } from "react";
import "./rangeSlider.css";
import {
  getRangeSliderValues,
  percentageToPointConversion,
  setRangeSliderValues,
  useIsMobile,
} from "../../store/user";
import { useSelector } from "react-redux";
import { selectIsResetOpen } from "../../reduxSlices/resetToggleSlice";
import {
  applyLineSpacing,
  applyLetterSpacing,
  applyWordSpacing,
} from "../../host/actions";

const THUMB_SIZE_REM = 1.625;

const RangeSlider = ({ data, widgetColor }) => {
  const isResetOpen = useSelector(selectIsResetOpen);
  const isMobile = useIsMobile();

  const extractColors = (gradient) => {
    const matches =
      typeof gradient === "string"
        ? gradient.match(/#(?:[0-9a-fA-F]{3}){1,2}/g)
        : null;
    const [, secondColor] = matches || ["#000000", "#000000"];
    return { secondColor };
  };

  function lineHeightFromPercent(pct) {
    const p = Math.max(0, Math.min(200, pct));

    const MIN = 1.0;
    const DEFAULT = 1.45;
    const MAX = 2.2;

    if (p === 100) return DEFAULT;
    if (p < 100) return MIN + (DEFAULT - MIN) * (p / 100);

    return DEFAULT + (MAX - DEFAULT) * ((p - 100) / 100);
  }

  const { secondColor } = extractColors(widgetColor);

  const [value, setValue] = useState("");
  const [valueChanged, setValueChanged] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  const getDefaultValue = () => {
    return data?.fieldName === "wordSpacing" ||
      data?.fieldName === "letterSpacing"
      ? 0
      : 100;
  };

  const getCurrentValue = () => {
    return value === "" ? getDefaultValue() : value;
  };

  const applySliderAction = (fieldName, rawValue) => {
    const numericValue = Number(rawValue);

    if (fieldName === "letterSpacing") {
      applyLetterSpacing(percentageToPointConversion(numericValue));
      return;
    }

    if (fieldName === "wordSpacing") {
      applyWordSpacing(percentageToPointConversion(numericValue));
      return;
    }

    if (fieldName === "lineSpacing") {
      applyLineSpacing(lineHeightFromPercent(numericValue));
    }
  };

  useEffect(() => {
    initialization();
  }, [data]);

  const initialization = () => {
    try {
      const storedData = getRangeSliderValues();
      const parsedData = storedData ? JSON.parse(storedData) : {};
      const field = data?.fieldName;

      if (parsedData[field] !== undefined) {
        const storedValue = parsedData[field];
        setValue(storedValue);
        setInputValue(String(storedValue));
      } else {
        const defaultValue = getDefaultValue();
        setValue(defaultValue);
        setInputValue(String(defaultValue));
      }
    } catch (error) {
      console.error("Initialization error:", error);
      const defaultValue = getDefaultValue();
      setValue(defaultValue);
      setInputValue(String(defaultValue));
    }
  };

  useEffect(() => {
    if (value === "") return;

    try {
      const storedData = getRangeSliderValues();
      const parsedData = storedData ? JSON.parse(storedData) : {};
      const updatedData = { ...parsedData, [data?.fieldName]: value };

      setRangeSliderValues(updatedData);
      localStorage.setItem("rangeSliderValues", JSON.stringify(updatedData));

      applySliderAction(data?.fieldName, value);
    } catch (error) {
      console.error("Value change error:", error);
    }
  }, [valueChanged]);

  useEffect(() => {
    if (isResetOpen) {
      localStorage.removeItem("rangeSliderValues");
      initialization();
    }
  }, [isResetOpen]);

  const handleChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    setValue(newValue);
    setValueChanged(newValue);
    setInputValue(String(newValue));
  };

  const increaseValue = () => {
    const newValue = Math.min(200, getCurrentValue() + 1);
    setValue(newValue);
    setValueChanged(newValue);
    setInputValue(String(newValue));
  };

  const decreaseValue = () => {
    const newValue = Math.max(0, getCurrentValue() - 1);
    setValue(newValue);
    setValueChanged(newValue);
    setInputValue(String(newValue));
  };

  const commitInputValue = (raw) => {
    let newValue = parseInt(raw, 10);
    if (isNaN(newValue)) newValue = 0;
    newValue = Math.min(200, Math.max(0, newValue));

    setValue(newValue);
    setValueChanged(newValue);
    setInputValue(String(newValue));
    setIsInputFocused(false);
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") {
      commitInputValue(e.target.value);
      e.target.blur();
    }
  };

  const currentValue = getCurrentValue();
  const sliderColorPercentage = Math.max(
    0,
    Math.min(100, (currentValue / 200) * 100),
  );

  const activeColor =
    typeof widgetColor === "string" && widgetColor.includes("linear-gradient")
      ? secondColor || "var(--widget-background)"
      : widgetColor || "var(--widget-background)";

  const trackSideInset = `${THUMB_SIZE_REM / 2}rem`;

  return (
    <div
      className={`${
        isMobile
          ? "w-full flex flex-col"
          : "flex justify-between items-center w-full"
      }`}
    >
      <div className="flex gap-3 justify-between items-center flex-1 min-w-0">
        <div
          style={{ background: activeColor }}
          className={`${
            currentValue === 0 || inputValue === "0"
              ? "opacity-50 cursor-default"
              : "cursor-pointer"
          } rounded-full flex items-center justify-center shrink-0 ${
            isMobile
              ? "min-w-[2.75rem] min-h-[2.75rem]"
              : "min-w-[2rem] min-h-[2rem]"
          }`}
          onClick={currentValue === 0 ? null : decreaseValue}
        >
          <div
            style={{ backgroundImage: "url('/rangeSlider/decreaseIcon.svg')" }}
            className="w-4 h-4 bg-no-repeat bg-center bg-contain"
          />
        </div>

        {!isMobile ? (
          <div className="w-[14.375rem] min-w-0">
            <div className="relative w-full h-[1.625rem] flex items-center">
              <div
                className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-[#d9d9d9]"
                style={{
                  left: trackSideInset,
                  right: trackSideInset,
                }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full"
                style={{
                  left: trackSideInset,
                  width: `calc((100% - ${trackSideInset} - ${trackSideInset}) * ${sliderColorPercentage / 100})`,
                  background: activeColor,
                }}
              />
              <input
                type="range"
                className="sliderThumbWhite relative w-full"
                min="0"
                max="200"
                step="1"
                value={currentValue}
                onChange={handleChange}
              />
            </div>
          </div>
        ) : (
          <div
            className={`transition-all duration-300 flex items-center bg-primary-background rounded-[0.5rem] h-[2.625rem] w-[7rem] shrink-0 overflow-hidden ${
              isInputFocused
                ? "border border-widget-background"
                : "border border-[#d8dbe8]"
            }`}
          >
            <input
              type="text"
              className="w-[4.75rem] h-full bg-transparent text-center text-text-black outline-none"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => {
                setInputValue(String(currentValue));
                setIsInputFocused(true);
              }}
              onBlur={(e) => commitInputValue(e.target.value)}
              maxLength={3}
            />
            <span className="w-[2.25rem] h-full flex items-center justify-center border-l border-[#d8dbe8] text-text-black">
              %
            </span>
          </div>
        )}

        <div
          style={{ background: activeColor }}
          className={`${
            currentValue === 200 || inputValue === "200"
              ? "opacity-50 cursor-default"
              : "cursor-pointer"
          } rounded-full flex items-center justify-center shrink-0 ${
            isMobile
              ? "min-w-[2.75rem] min-h-[2.75rem]"
              : "min-w-[2rem] min-h-[2rem]"
          }`}
          onClick={currentValue === 200 ? null : increaseValue}
        >
          <div
            style={{ backgroundImage: "url('/rangeSlider/increaseIcon.svg')" }}
            className="w-4 h-4 bg-no-repeat bg-center bg-contain"
          />
        </div>
      </div>

      {!isMobile ? (
        <div className="ml-[0.75rem] flex items-center bg-primary-background rounded-[0.5rem] h-[2.625rem] w-[7rem] border border-[#d8dbe8] shrink-0 overflow-hidden">
          <input
            type="text"
            className="w-[4.75rem] h-full bg-transparent text-center text-text-black outline-none"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setInputValue(String(currentValue))}
            onBlur={(e) => commitInputValue(e.target.value)}
            onKeyDown={handleEnterKey}
            maxLength={3}
          />
          <span className="w-[2.25rem] h-full flex items-center justify-center border-l border-[#d8dbe8] text-text-black">
            %
          </span>
        </div>
      ) : (
        <div className="w-full mt-[1.88rem] mb-[1.5rem]">
          <div className="relative w-full h-[1.625rem] flex items-center">
            <div
              className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-[#d9d9d9]"
              style={{
                left: trackSideInset,
                right: trackSideInset,
              }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full"
              style={{
                left: trackSideInset,
                width: `calc((100% - ${trackSideInset} - ${trackSideInset}) * ${sliderColorPercentage / 100})`,
                background: activeColor,
              }}
            />
            <input
              type="range"
              className="sliderThumbWhite relative w-full"
              min="0"
              max="200"
              step="1"
              value={currentValue}
              onKeyDown={handleEnterKey}
              onChange={handleChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeSlider;
